import React, { useEffect, useState, useMemo, useContext } from "react";
import axios from "axios";
import { useOutletContext } from "react-router-dom";
import { FaEdit, FaSearch, FaHorseHead, FaSave, FaTimes } from "react-icons/fa";
import { UserContext } from "../../context/UserContext";
import "../../styles/associadosCircuito.css";

export default function CircuitoCavalos() {
  const [cavalos, setCavalos] = useState([]);

  const { circuito } = useOutletContext();
  const circuitoId = circuito?._id;

  const { token: tokenCtx } = useContext(UserContext);
  const getToken = () => tokenCtx || localStorage.getItem("token");

  const [loading, setLoading] = useState(true);
  const [salvando, setSalvando] = useState(false);

  // Filtros
  const [busca, setBusca] = useState("");
  const [filtroCategoria, setFiltroCategoria] = useState("");
  const [filtroTipo, setFiltroTipo] = useState(""); // "", "avulso", "vaqueiro"
  const [filtroStatus, setFiltroStatus] = useState("");

  const [paginaAtual, setPaginaAtual] = useState(1);
  const itensPorPagina = 10;

  // Modal de edição
  const [modalOpen, setModalOpen] = useState(false);
  const [editando, setEditando] = useState(null);
  const [form, setForm] = useState({ nomeCavalo: "", haras: "", responsavel: "", categoria: "" });

  useEffect(() => {
    if (circuitoId) buscarCavalos();
  }, [circuitoId]);

  useEffect(() => {
    setPaginaAtual(1);
  }, [busca, filtroCategoria, filtroTipo, filtroStatus]);

  async function buscarCavalos() {
    try {
      setLoading(true);
      const token = getToken();
      const { data } = await axios.get(
        `https://api.ticketdovaqueiro.com/api/circuitos/${circuitoId}/cavalos`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setCavalos(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error("Erro ao buscar cavalos:", error);
    } finally {
      setLoading(false);
    }
  }

  function abrirEdicao(cavalo) {
    setEditando(cavalo);
    setForm({
      nomeCavalo: cavalo.nomeCavalo || "",
      haras: cavalo.haras || "",
      responsavel: cavalo.responsavel || "",
      categoria: cavalo.categoria || "",
    });
    setModalOpen(true);
  }

  function fecharEdicao() {
    setModalOpen(false);
    setEditando(null);
  }

  async function salvarEdicao() {
    if (!form.nomeCavalo.trim() || !form.categoria) {
      alert("Informe ao menos o nome do cavalo e a categoria.");
      return;
    }
    setSalvando(true);
    try {
      const token = getToken();
      await axios.put(
        `https://api.ticketdovaqueiro.com/api/circuitos/${circuitoId}/cavalos/${editando._id}`,
        form,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      fecharEdicao();
      buscarCavalos();
    } catch (error) {
      alert(error.response?.data?.erro || "Erro ao salvar alterações.");
    } finally {
      setSalvando(false);
    }
  }

  const removerAcentos = (str) => {
    if (!str) return "";
    return str.normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase();
  };

  const categoriasUnicas = useMemo(() => {
    const cats = cavalos.map((c) => c.categoria).filter(Boolean);
    return [...new Set(cats)].sort();
  }, [cavalos]);

  const cavalosFiltrados = useMemo(() => {
    return cavalos.filter((c) => {
      const termo = removerAcentos(busca);
      const matchTexto =
        removerAcentos(c.nomeCavalo).includes(termo) ||
        removerAcentos(c.haras).includes(termo) ||
        removerAcentos(c.responsavel).includes(termo);

      const matchCategoria = filtroCategoria ? c.categoria === filtroCategoria : true;
      const matchTipo = filtroTipo
        ? (filtroTipo === "avulso" ? c.isAvulso : !c.isAvulso)
        : true;
      const matchStatus = filtroStatus ? c.statusPagamento === filtroStatus : true;

      return matchTexto && matchCategoria && matchTipo && matchStatus;
    });
  }, [cavalos, busca, filtroCategoria, filtroTipo, filtroStatus]);

  const cavalosOrdenados = useMemo(() => {
    return [...cavalosFiltrados].sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
  }, [cavalosFiltrados]);

  const indexUltimoItem = paginaAtual * itensPorPagina;
  const indexPrimeiroItem = indexUltimoItem - itensPorPagina;
  const cavalosPaginados = cavalosOrdenados.slice(indexPrimeiroItem, indexUltimoItem);
  const totalPaginas = Math.ceil(cavalosOrdenados.length / itensPorPagina);

  return (
    <div className="pc-associados">
      <div className="pc-associados-header">
        <h2 className="pc-title" style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <FaHorseHead /> Cavalos do Circuito
        </h2>
      </div>

      <div className="pc-filters-container">
        <div className="pc-filter-group" style={{ flex: 1.5 }}>
          <label>Buscar Cavalo, Haras ou Responsável</label>
          <div className="pc-filter-input-wrapper">
            <FaSearch />
            <input
              type="text"
              className="pc-filter-input"
              placeholder="Digite para buscar..."
              value={busca}
              onChange={(e) => setBusca(e.target.value)}
            />
          </div>
        </div>

        <div className="pc-filter-group">
          <label>Tipo</label>
          <select className="pc-filter-select" value={filtroTipo} onChange={(e) => setFiltroTipo(e.target.value)}>
            <option value="">Todos</option>
            <option value="avulso">Cavalo Avulso (sem vaqueiro)</option>
            <option value="vaqueiro">Com Vaqueiro</option>
          </select>
        </div>

        <div className="pc-filter-group">
          <label>Categoria</label>
          <select className="pc-filter-select" value={filtroCategoria} onChange={(e) => setFiltroCategoria(e.target.value)}>
            <option value="">Todas as categorias</option>
            {categoriasUnicas.map((cat) => (
              <option key={cat} value={cat}>{cat}</option>
            ))}
          </select>
        </div>

        <div className="pc-filter-group">
          <label>Status</label>
          <select className="pc-filter-select" value={filtroStatus} onChange={(e) => setFiltroStatus(e.target.value)}>
            <option value="">Todos</option>
            <option value="pago">Pago</option>
            <option value="pendente">Pendente</option>
            <option value="cancelado">Cancelado</option>
          </select>
        </div>
      </div>

      <div className="pc-card" style={{ padding: 0, overflow: "hidden" }}>
        <div style={{ overflowX: "auto", width: "100%" }}>
          <table className="pc-table" style={{ minWidth: "1000px", margin: 0 }}>
            <thead>
              <tr>
                <th>Nome do Cavalo</th>
                <th>Haras / Representação</th>
                <th>Responsável / Vaqueiro</th>
                <th>Categoria</th>
                <th>Tipo</th>
                <th>Taxa do Cavalo</th>
                <th>Status</th>
                <th>Data</th>
                <th>Ações</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan="9" style={{ textAlign: "center", padding: "20px" }}>Carregando...</td></tr>
              ) : cavalosPaginados.length === 0 ? (
                <tr><td colSpan="9" style={{ textAlign: "center", padding: "40px 20px", color: "rgba(255,255,255,0.5)" }}>Nenhum cavalo encontrado para os filtros aplicados.</td></tr>
              ) : (
                cavalosPaginados.map((c) => (
                  <tr key={c._id}>
                    <td style={{ fontWeight: 600 }}>{c.nomeCavalo || "—"}</td>
                    <td>{c.haras || "—"}</td>
                    <td>{c.responsavel || "—"}</td>
                    <td>{c.categoria}</td>
                    <td>
                      <span style={{
                        fontSize: 11, fontWeight: 700, padding: "4px 10px", borderRadius: 999,
                        textTransform: "uppercase", letterSpacing: "0.3px",
                        background: c.isAvulso ? "rgba(245,158,11,0.15)" : "rgba(59,130,246,0.15)",
                        color: c.isAvulso ? "#f59e0b" : "#3b82f6",
                      }}>
                        {c.isAvulso ? "Avulso" : "Com Vaqueiro"}
                      </span>
                    </td>
                    <td style={{ fontWeight: 800, color: c.isAvulso ? "#fcd34d" : "#c4b5fd" }}>
                      R$ {Number(c.valorCavalo || 0).toLocaleString("pt-BR", { minimumFractionDigits: 2 })}
                    </td>
                    <td><span className={`status ${c.statusPagamento}`}>{c.statusPagamento}</span></td>
                    <td>{c.createdAt ? new Date(c.createdAt).toLocaleDateString() : "—"}</td>
                    <td className="acoes" style={{ display: "flex", gap: "6px", alignItems: "center" }}>
                      <button className="btn-editar" title="Editar" onClick={() => abrirEdicao(c)}>
                        <FaEdit />
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {!loading && totalPaginas > 1 && (
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "16px 20px", borderTop: "1px solid rgba(255,255,255,0.05)", flexWrap: "wrap", gap: "10px" }}>
            <span style={{ fontSize: "13px", color: "rgba(255,255,255,0.5)" }}>
              Mostrando {indexPrimeiroItem + 1} até {Math.min(indexUltimoItem, cavalosOrdenados.length)} de {cavalosOrdenados.length} registros
            </span>
            <div style={{ display: "flex", gap: "8px" }}>
              <button
                onClick={() => setPaginaAtual((p) => Math.max(1, p - 1))}
                disabled={paginaAtual === 1}
                style={{ padding: "8px 16px", background: "rgba(255,255,255,0.05)", border: "none", color: "#fff", borderRadius: "8px", cursor: paginaAtual === 1 ? "not-allowed" : "pointer", opacity: paginaAtual === 1 ? 0.3 : 1 }}
              >
                Anterior
              </button>
              <div style={{ padding: "8px 16px", background: "var(--c-accent)", color: "#000", borderRadius: "8px", fontWeight: "bold", fontSize: "14px" }}>
                {paginaAtual} de {totalPaginas}
              </div>
              <button
                onClick={() => setPaginaAtual((p) => Math.min(totalPaginas, p + 1))}
                disabled={paginaAtual === totalPaginas}
                style={{ padding: "8px 16px", background: "rgba(255,255,255,0.05)", border: "none", color: "#fff", borderRadius: "8px", cursor: paginaAtual === totalPaginas ? "not-allowed" : "pointer", opacity: paginaAtual === totalPaginas ? 0.3 : 1 }}
              >
                Próxima
              </button>
            </div>
          </div>
        )}
      </div>

      {/* MODAL DE EDIÇÃO */}
      {modalOpen && editando && (
        <div className="modal-overlay">
          <div className="modal">
            <h3 style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <FaEdit /> Editar {editando.isAvulso ? "Cavalo Avulso" : "Inscrição"}
            </h3>

            <div style={{ display: "flex", flexDirection: "column", gap: 16, marginTop: 16 }}>
              <div>
                <label style={{ display: "block", marginBottom: 6, fontSize: 13, color: "rgba(255,255,255,0.7)" }}>
                  Nome do Cavalo
                </label>
                <input
                  type="text"
                  className="c-input"
                  style={{ width: "100%", height: 44, paddingLeft: 14 }}
                  value={form.nomeCavalo}
                  onChange={(e) => setForm({ ...form, nomeCavalo: e.target.value })}
                />
              </div>

              <div>
                <label style={{ display: "block", marginBottom: 6, fontSize: 13, color: "rgba(255,255,255,0.7)" }}>
                  {editando.isAvulso ? "Haras" : "Representação / Equipe"}
                </label>
                <input
                  type="text"
                  className="c-input"
                  style={{ width: "100%", height: 44, paddingLeft: 14 }}
                  value={form.haras}
                  onChange={(e) => setForm({ ...form, haras: e.target.value })}
                />
              </div>

              <div>
                <label style={{ display: "block", marginBottom: 6, fontSize: 13, color: "rgba(255,255,255,0.7)" }}>
                  {editando.isAvulso ? "Responsável" : "Nome do Vaqueiro"}
                </label>
                <input
                  type="text"
                  className="c-input"
                  style={{ width: "100%", height: 44, paddingLeft: 14 }}
                  value={form.responsavel}
                  onChange={(e) => setForm({ ...form, responsavel: e.target.value })}
                />
              </div>

              <div>
                <label style={{ display: "block", marginBottom: 6, fontSize: 13, color: "rgba(255,255,255,0.7)" }}>
                  Categoria
                </label>
                <select
                  className="c-input"
                  style={{ width: "100%", height: 44, paddingLeft: 14 }}
                  value={form.categoria}
                  onChange={(e) => setForm({ ...form, categoria: e.target.value })}
                >
                  <option value="">Selecione...</option>
                  {(circuito?.categorias || []).map((cat, i) => {
                    const nome = typeof cat === "string" ? cat : cat.nome;
                    return <option key={i} value={nome}>{nome}</option>;
                  })}
                </select>
              </div>
            </div>

            <div style={{ display: "flex", gap: 10, marginTop: 24 }}>
              <button
                onClick={fecharEdicao}
                disabled={salvando}
                style={{ flex: 1, height: 46, borderRadius: 10, border: "1px solid rgba(255,255,255,0.15)", background: "transparent", color: "#fff", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", gap: 8 }}
              >
                <FaTimes /> Cancelar
              </button>
              <button
                onClick={salvarEdicao}
                disabled={salvando}
                style={{ flex: 1, height: 46, borderRadius: 10, border: "none", background: "var(--c-accent)", color: "#000", fontWeight: 700, cursor: salvando ? "wait" : "pointer", display: "flex", alignItems: "center", justifyContent: "center", gap: 8 }}
              >
                <FaSave /> {salvando ? "Salvando..." : "Salvar"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}